import { mapActions, mapGetters } from 'vuex'

import { Colors, generateUniqueKey } from '@sigma-frontend-libs/commons'

import { FILE_TYPES, audioStatuses, getStorageURL } from '@/utils'

export const dialogTypes = {
  editMeta: 'editMeta',
  remove: 'remove',
  edit: 'edit',
  source: 'source',
  export: 'export'
}

export const fileInfoIcons = {
  id: '123',
  hash: 'more_horiz',
  size: 'dns',
  type: 'type_specimen',
  owner: 'person_outline',
  createdAt: 'access_time',
  updatedAt: 'access_time',
  willBeDeleted: 'history'
}

export const getFileMiscInfo = file => {
  const result = {
    type: FILE_TYPES.file.value,
    color: FILE_TYPES.file.color
  }

  if (file) {
    if (/(ms-?word|wordprocessingml)/.test(file.type)) {
      result.type = FILE_TYPES.doc.value
      result.color = FILE_TYPES.doc.color
    }
    if (/(ms-excel|spreadsheetml)/.test(file.type)) {
      result.type = FILE_TYPES.csv.value
      result.color = FILE_TYPES.csv.color
    }
    if (/.*?\/csv/.test(file.type)) {
      result.type = FILE_TYPES.csv.value
      result.color = FILE_TYPES.csv.color
    }
    if (/.*?\/pdf/.test(file.type)) {
      result.type = FILE_TYPES.pdf.value
      result.color = FILE_TYPES.pdf.color
    }
    if (/image\//.test(file.type)) {
      result.type = FILE_TYPES.jpg.value
      result.color = FILE_TYPES.jpg.color
    }
    if (/audio\//.test(file.type)) {
      result.type = FILE_TYPES.wav.value
      result.color = FILE_TYPES.wav.color
    }
    if (/video\//.test(file.type)) {
      result.type = FILE_TYPES.mp4.value
      result.color = FILE_TYPES.mp4.color
    }
  }

  return result
}

export const getFileSizeColor = size => {
  switch (true) {
    case size < 1024: return Colors.file
    case size < 1024 * 1024: return Colors.success
    case size < 1024 * 1024 * 1024: return Colors.warning
    case size < 1024 * 1024 * 1024 * 1024: return Colors.error
    case size < 1024 * 1024 * 1024 * 1024 * 1024: return Colors.legendary
    case size < 1024 * 1024 * 1024 * 1024 * 1024 * 1024: return Colors.epic
    default: return Colors.file
  }
}

export const filePlayer = {
  props: {
    value: {
      type: [ String, Object ],
      default: () => {
        return {}
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    autoplay: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      localPlayerId: generateUniqueKey()
    }
  },

  computed: {
    ...mapGetters({
      src: 'audio/src',
      currentTime: 'audio/currentTime',
      duration: 'audio/duration',
      status: 'audio/status',
      playerId: 'audio/playerId'
    }),

    sourceUrl() {
      switch (typeof this.value) {
        case 'object': return getStorageURL([ this.value.OwnerId || this.value.Owner && this.value.Owner.id, this.value.id || this.value.fileId ])
        case 'string':
        default: return getStorageURL([ this.value ])
      }
    },

    isCurrentSource() {
      return this.src === this.sourceUrl
    },

    isCurrentPlayedSource() {
      return this.isCurrentSource && this.playerId === this.localPlayerId
    },

    sourceDuration() {
      if (this.isCurrentPlayedSource && this.value && this.duration) {
        return Math.round(this.duration * 1000)
      }
    },

    sourceCurrentTime() {
      if (this.isCurrentPlayedSource && this.currentTime && this.sourceDuration) {
        return this.sourceDuration - this.currentTime * 1000
      }
    },

    sourceProgress() {
      if (this.isCurrentPlayedSource && this.currentTime && this.sourceDuration) {
        return Math.round(this.currentTime * 1000 / this.sourceDuration * 100)
      }
    },

    playing() {
      return this.isCurrentPlayedSource && this.status === audioStatuses.play
    },

    loadingAudio() {
      return this.isCurrentPlayedSource && this.status === audioStatuses.loading
    }
  },

  mounted() {
    if (this.autoplay && !this.disabled && !this.isCurrentSource) {
      this.setPlayerId(this.localPlayerId)
      this.setSrc(this.sourceUrl)
    }
  },

  beforeDestroy() {
    if (this.isCurrentSource || this.isCurrentPlayedSource) {
      this.setPlayerId(undefined)
      this.setSrc(undefined)
    }
  },

  methods: {
    ...mapActions({
      setSrc: 'audio/setSrc',
      setPlayerId: 'audio/setPlayerId'
    })
  }
}
